/**
 * Document Picture-in-Picture window helper.
 *
 * `documentPictureInPicture.requestWindow()` opens a blank, always-on-top
 * document. React content portaled into it has no styles and no theme/RTL
 * state, so we copy the shell's stylesheets and keep `<html>` attributes
 * mirrored for as long as the window lives.
 */

/** Minimal local typing; TS ships no lib types for this API yet. */
interface DocumentPipApi {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
}

/** Initial size of the call PiP window. */
export const PIP_DEFAULT_SIZE = { width: 300, height: 260 } as const;

const MIRRORED_ATTRIBUTES = ["class", "dir", "lang", "style"] as const;

/**
 * Clone every `<style>` and `<link rel="stylesheet">` of `from` into
 * `to.head`. Constructed sheets with no owner node are serialized from their
 * rules; a sheet whose rules throw (cross-origin) is skipped.
 */
export function copyStyles(from: Document, to: Document): void {
  // Node-backed sheets are cloned so media attributes survive. A link's href
  // is made absolute: the PiP document is about:blank, so a relative
  // "/assets/…css" would resolve against nothing and never load.
  from.querySelectorAll("style,link[rel=stylesheet]").forEach((node) => {
    // Tag check, not instanceof: the clone belongs to the PiP window's realm.
    const clone = to.importNode(node, true) as Element;
    const href = node.tagName === "LINK" ? (node as HTMLLinkElement).href : "";
    if (href) clone.setAttribute("href", href);
    to.head.appendChild(clone);
  });
  // Sheets with no owner node (constructed / adopted) need serializing.
  for (const sheet of Array.from(from.styleSheets)) {
    if (sheet.ownerNode) continue;
    try {
      const style = to.createElement("style");
      style.textContent = Array.from(sheet.cssRules)
        .map((r) => r.cssText)
        .join("\n");
      to.head.appendChild(style);
    } catch {
      // Cross-origin sheet: rules are unreadable, so skip it.
    }
  }
}

/**
 * Mirror `class`, `dir`, `lang` and `style` of `from.documentElement` onto
 * `to.documentElement` now and on every change. Returns a disconnect function.
 */
export function mirrorRootAttributes(from: Document, to: Document): () => void {
  const sync = () => {
    for (const name of MIRRORED_ATTRIBUTES) {
      const value = from.documentElement.getAttribute(name);
      if (value === null) to.documentElement.removeAttribute(name);
      else to.documentElement.setAttribute(name, value);
    }
  };
  sync();
  const observer = new MutationObserver(sync);
  observer.observe(from.documentElement, {
    attributes: true,
    attributeFilter: [...MIRRORED_ATTRIBUTES],
  });
  return () => observer.disconnect();
}

/** Open a styled Document PiP window; mirroring stops on its `pagehide`. */
export async function openDocumentPip(size: {
  width: number;
  height: number;
}): Promise<Window> {
  const api = (window as unknown as { documentPictureInPicture?: DocumentPipApi })
    .documentPictureInPicture;
  if (!api) throw new Error("Document Picture-in-Picture is not supported");
  const pip = await api.requestWindow(size);
  copyStyles(document, pip.document);
  const stop = mirrorRootAttributes(document, pip.document);
  pip.addEventListener("pagehide", stop, { once: true });
  return pip;
}
